import watchListActions from "security-content/mappings/externalActions";
import watchlists from "security-content/mappings/watchlists";
import {
  WatchlistAddition,
  WatchlistRemoval,
  WatchlistActionHelp
} from "security-content/externalActionHandlers/WatchlistStatusChange";
import { replaceTemplateWithFieldValue } from "security-content/externalActionHandlers/ExternalLinks";
import { isArrayValue } from "search/messageFieldValueHelper";
import useLicenseCheck from "license/useLicenseCheck";
import useExternalActionsQuery from "security-content/hooks/useExternalActionsQuery";
import useWatchlistMutations from "watchlist/hooks/useWatchlistMutations";
import hasMultipleValueForActions from "views/components/visualizations/utils/hasMultipleValueForActions";
const assertUnreachable = (ignored) => {
  throw new Error(`Unexpected action definition: ${ignored}`);
};
const licenseIsValid = (licenseCheck) => {
  var _a;
  return !!((_a = licenseCheck == null ? void 0 : licenseCheck.security) == null ? void 0 : _a.isValid);
};
const createLookupAction = (name, lookupAction) => ({
  type: name,
  title: lookupAction.title,
  isHidden: ({ field, contexts: { licenseCheck } }) => !licenseIsValid(licenseCheck) || !lookupAction.fieldNames.includes(field),
  resetFocus: false,
  linkTarget: replaceTemplateWithFieldValue(lookupAction.options.linkTemplate)
});
const createWatchlistActions = (name, watchlistAction, { addToWatchlist, removeFromWatchlist }) => {
  const wlFieldType = watchlistAction.options.fieldType;
  const watchlist = Object.values(watchlists).find((wl) => wl.fieldType === wlFieldType);
  const isEnabled = ({ value }) => !isArrayValue(value);
  const commonProperties = {
    type: name,
    isEnabled,
    help: (handlerArgs) => WatchlistActionHelp(isEnabled(handlerArgs)),
    resetFocus: false
  };
  return [
    {
      ...commonProperties,
      title: `Add to ${watchlist.title}`,
      isHidden: ({ field, contexts, contexts: { currentUser, watchlistStatus, licenseCheck } }) => hasMultipleValueForActions(contexts) || !licenseIsValid(licenseCheck) || WatchlistAddition.isHidden(wlFieldType, field, currentUser == null ? void 0 : currentUser.permissions, watchlistStatus == null ? void 0 : watchlistStatus.status),
      handler: WatchlistAddition.handler(wlFieldType, addToWatchlist)
    },
    {
      ...commonProperties,
      title: `Remove from ${watchlist.title}`,
      isHidden: ({ field, contexts, contexts: { currentUser, watchlistStatus, licenseCheck } }) => hasMultipleValueForActions(contexts) || !licenseIsValid(licenseCheck) || WatchlistRemoval.isHidden(wlFieldType, field, currentUser == null ? void 0 : currentUser.permissions, watchlistStatus == null ? void 0 : watchlistStatus.status),
      handler: WatchlistRemoval.handler(wlFieldType, removeFromWatchlist, watchlist.title)
    }
  ];
};
const createActions = (actionDefinitions, watchlistMutations) => Object.entries(actionDefinitions).flatMap(([name, actionDefinition]) => {
  switch (actionDefinition.type) {
    case "http-get":
      return createLookupAction(name, actionDefinition);
    case "lookup-table":
      return createWatchlistActions(name, actionDefinition, watchlistMutations);
    default:
      return assertUnreachable(actionDefinition);
  }
});
export default function useExternalActions() {
  const {
    security: { isValid: isValidSecurityLicense }
  } = useLicenseCheck();
  const watchlistMutations = useWatchlistMutations();
  const { data, error, isFetching, isError } = useExternalActionsQuery({
    isValidSecurityLicense,
    isLoadingSecurityLicense: false
  });
  let externalValueActions = null;
  if (isFetching || isError || !isValidSecurityLicense) {
    externalValueActions = [];
  } else {
    externalValueActions = createActions({ ...watchListActions, ...data }, watchlistMutations);
  }
  return {
    error,
    externalValueActions,
    isError,
    isLoading: isFetching
  };
}
